import { useCallback, useEffect, useRef, useState } from 'react';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';

import type { BillingReadinessCategory } from '../services/api/types.gen';
import { useStore } from '../store';
import {
  BILLING_TAB_DEFAULT_SORTING,
  type BillingTabKey,
} from '../store/slices/billingUiSlice';

/** Server request params produced from the per-tab query state. */
export interface BillingTableQueryParams {
  search: string;
  sort_by: string | undefined;
  sort_order: 'asc' | 'desc' | undefined;
  /** 1-based page index for the backend. */
  page: number;
  per_page: number;
  insurance_ids: string[];
  clinic_ids: string[];
  /** Not Ready only; omitted on every other tab. */
  failing_categories?: BillingReadinessCategory[];
  /** Page-wide flag from billingUiSlice; sent on every list request. */
  hide_test_patients: boolean;
  /** Page-wide flag from billingUiSlice (EMR-2099); sent on every list request. */
  show_inactive_episodes: boolean;
  /** Imported patient charts are excluded by default on every claims list. */
  hide_imported_patients: boolean;
}

export interface UseBillingTableQueryOptions {
  /**
   * Per-column backend sort key. A column whose tanstack `id` differs from its
   * backend `sort_by` (e.g. `billing_period` -> `start_date`) is listed here.
   * Columns not listed use their `id` as the sort key (identity), which is the
   * common case (`patient_name`, `claim_age`, ...).
   */
  sortKeyByColumnId?: Record<string, string>;
}

/**
 * Owns a billing tab's server-driven query state.
 *
 * Persisted fields (`globalFilter`/`insuranceIds`/`clinicIds`/`sorting`/
 * `pageSize`) live in the `billingUiSlice`; the non-persisted `pageIndex` lives
 * here and resets to 0 on any non-page change (search/sort/filter/perPage) to
 * avoid stale-page requests.
 */
export interface BillingTableQuery {
  search: string;
  sorting: SortingState;
  /** 0-based page index (tanstack convention). */
  page: number;
  perPage: number;
  insuranceIds: string[];
  clinicIds: string[];
  failingCategories: BillingReadinessCategory[];
  setSearch: (value: string) => void;
  /** Single-sort: accepts a value or a tanstack updater; only sorting[0] is kept. */
  setSorting: OnChangeFn<SortingState>;
  setPage: (pageIndex: number) => void;
  setPerPage: (perPage: number) => void;
  setInsuranceIds: (ids: string[]) => void;
  setClinicIds: (ids: string[]) => void;
  setFailingCategories: (values: BillingReadinessCategory[]) => void;
  reset: () => void;
  toParams: () => BillingTableQueryParams;
}

/** Module-level constant = a STABLE reference for tabs whose stored entry
 *  predates `failingCategories`. Zustand compares snapshots by identity, so a
 *  fresh `[]` per read triggers React's "getSnapshot should be cached" loop. */
const EMPTY_FAILING_CATEGORIES: BillingReadinessCategory[] = [];

export function useBillingTableQuery(
  tabKey: BillingTabKey,
  options: UseBillingTableQueryOptions = {}
): BillingTableQuery {
  const ui = useStore(state => state.getBillingTabUi(tabKey));
  const hideTestPatients = useStore(state => state.hideTestPatients);
  const showInactiveEpisodes = useStore(state => state.showInactiveEpisodes);
  const hideImportedPatients = useStore(state => state.hideImportedPatients);
  const setUi = useStore(state => state.setBillingTabUi);
  const resetUi = useStore(state => state.resetBillingTabUi);
  const [pageIndex, setPageIndex] = useState(0);
  const previousHideTestPatients = useRef(hideTestPatients);
  const previousShowInactiveEpisodes = useRef(showInactiveEpisodes);
  const previousHideImportedPatients = useRef(hideImportedPatients);

  useEffect(() => {
    if (
      previousHideTestPatients.current !== hideTestPatients ||
      previousShowInactiveEpisodes.current !== showInactiveEpisodes
    ) {
      previousHideTestPatients.current = hideTestPatients;
      previousShowInactiveEpisodes.current = showInactiveEpisodes;
      setPageIndex(0);
    }
  }, [hideTestPatients, showInactiveEpisodes]);

  useEffect(() => {
    if (previousHideImportedPatients.current !== hideImportedPatients) {
      previousHideImportedPatients.current = hideImportedPatients;
      setPageIndex(0);
    }
  }, [hideImportedPatients]);

  const failingCategories = ui.failingCategories ?? EMPTY_FAILING_CATEGORIES;

  const sortKeyByColumnId = options.sortKeyByColumnId;

  const setSearch = useCallback(
    (value: string) => {
      setPageIndex(0);
      setUi(tabKey, { globalFilter: value });
    },
    [tabKey, setUi]
  );

  const setSorting = useCallback<OnChangeFn<SortingState>>(
    updater => {
      setPageIndex(0);
      const resolved =
        typeof updater === 'function' ? updater(ui.sorting) : updater;
      // The backend is single-sort: keep only the first sort entry. Clearing the
      // sort (TanStack's third header click) would send no sort_by, which the
      // backend reads as end_date DESC — the opposite of the tab's claim_age DESC
      // default — so restore the tab default instead of an empty sort.
      setUi(tabKey, {
        sorting:
          resolved.length > 0
            ? [resolved[0]]
            : BILLING_TAB_DEFAULT_SORTING[tabKey],
      });
    },
    [tabKey, setUi, ui.sorting]
  );

  const setPage = useCallback((index: number) => {
    setPageIndex(index);
  }, []);

  const setPerPage = useCallback(
    (perPage: number) => {
      setPageIndex(0);
      setUi(tabKey, { pageSize: perPage });
    },
    [tabKey, setUi]
  );

  const setInsuranceIds = useCallback(
    (ids: string[]) => {
      setPageIndex(0);
      setUi(tabKey, { insuranceIds: ids });
    },
    [tabKey, setUi]
  );

  const setClinicIds = useCallback(
    (ids: string[]) => {
      setPageIndex(0);
      setUi(tabKey, { clinicIds: ids });
    },
    [tabKey, setUi]
  );

  const setFailingCategories = useCallback(
    (values: BillingReadinessCategory[]) => {
      setPageIndex(0);
      setUi(tabKey, { failingCategories: values });
    },
    [tabKey, setUi]
  );

  const reset = useCallback(() => {
    setPageIndex(0);
    resetUi(tabKey);
  }, [tabKey, resetUi]);

  const toParams = useCallback((): BillingTableQueryParams => {
    const sort = ui.sorting[0];
    const sortBy = sort ? (sortKeyByColumnId?.[sort.id] ?? sort.id) : undefined;
    return {
      search: ui.globalFilter,
      sort_by: sortBy,
      sort_order: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
      page: pageIndex + 1,
      per_page: ui.pageSize,
      insurance_ids: ui.insuranceIds,
      clinic_ids: ui.clinicIds,
      // Only Not Ready filters by failure category; the ready/review request
      // and every submitted tab must omit the field entirely.
      ...(tabKey === 'not-ready'
        ? { failing_categories: failingCategories }
        : {}),
      hide_test_patients: hideTestPatients,
      show_inactive_episodes: showInactiveEpisodes,
      hide_imported_patients: hideImportedPatients,
    };
  }, [
    ui,
    pageIndex,
    sortKeyByColumnId,
    tabKey,
    failingCategories,
    hideTestPatients,
    showInactiveEpisodes,
    hideImportedPatients,
  ]);

  return {
    search: ui.globalFilter,
    sorting: ui.sorting,
    page: pageIndex,
    perPage: ui.pageSize,
    insuranceIds: ui.insuranceIds,
    clinicIds: ui.clinicIds,
    failingCategories,
    setSearch,
    setSorting,
    setPage,
    setPerPage,
    setInsuranceIds,
    setClinicIds,
    setFailingCategories,
    reset,
    toParams,
  };
}
